import Image from 'next/image'
import { IntentPrefetchLink } from '@/components/navigation/intent-prefetch-link'
import { FLOW_TYPE_LABEL } from '@/lib/flow-templates/flow-type-copy'
import { formatFlowCardSubtitle } from '@/lib/flow-templates/format-flow-timing'
import type { PublicFlowTemplateCard } from '@/lib/flow-templates/load-public-flow-templates'
import {
  FLOW_CARD_CLASSES,
  FLOW_CARD_STRIP_CLASSES,
  FLOW_CARD_STRIP_ITEM_CLASSES,
  FLOW_CARD_STRIP_SIZES,
} from '../lib/grid-classes'
import { TemplateBrandLogo } from './template-brand-logo'

const STRIP_SLOTS = [0, 1, 2] as const

/**
 * The first three emails side by side, same height, full opacity. Empty
 * slots keep the strip's rhythm so every card is the same height.
 */
function FlowCardStrip({
  thumbnails,
  brandName,
}: {
  thumbnails: PublicFlowTemplateCard['thumbnails']
  brandName: string
}) {
  return (
    <div className={FLOW_CARD_STRIP_CLASSES}>
      {STRIP_SLOTS.map((slot) => {
        const thumbnail = thumbnails[slot]
        return (
          <div key={slot} className={FLOW_CARD_STRIP_ITEM_CLASSES}>
            {thumbnail ? (
              <Image
                src={thumbnail.imagePngUrl}
                alt={`${brandName} email ${slot + 1}`}
                fill
                sizes={FLOW_CARD_STRIP_SIZES}
                quality={60}
                loading="lazy"
                className="object-cover object-top"
              />
            ) : null}
          </div>
        )
      })}
    </div>
  )
}

/**
 * One flow in the gallery: the first three emails side by side, then the
 * brand and what the sequence is — artwork over a caption, the way the
 * template cards beside it read. The whole card is the link — an intent
 * prefetch (pointer or focus) carries the flow's own page, so the click
 * paints the sequence instead of its skeleton.
 */
export function FlowCard({
  card,
  href,
}: {
  card: PublicFlowTemplateCard
  href: string
}) {
  const subtitle = formatFlowCardSubtitle({
    emailCount: card.emailCount,
    spanDays: card.spanDays,
  })
  return (
    <IntentPrefetchLink
      href={href}
      className={FLOW_CARD_CLASSES}
      aria-label={`${card.brandName} ${FLOW_TYPE_LABEL[card.flowType].toLowerCase()}: ${subtitle}`}
    >
      <FlowCardStrip thumbnails={card.thumbnails} brandName={card.brandName} />
      <div className="mt-3 flex w-full min-w-0 items-center gap-2.5">
        <TemplateBrandLogo
          domain={card.brandDomain}
          name={card.brandName}
          logoUrl={card.logoUrl}
          className="size-7 shrink-0"
        />
        <span className="flex min-w-0 flex-1 flex-col">
          <span className="truncate font-medium text-foreground text-sm">
            {card.brandName}
          </span>
          <span className="truncate text-muted-foreground text-xs">
            {subtitle}
          </span>
        </span>
      </div>
    </IntentPrefetchLink>
  )
}
